/**
 * The Dashboard's wording and arithmetic.
 *
 * The Dashboard is written for everyone on the team, not only for whoever
 * configures sources, so every number it shows is said in plain words: "Signing
 * in to the source" rather than `authenticating`, "On schedule" rather than
 * `scheduled`. That translation lives here, free of React, so
 * `tests/scraper-dashboard.test.ts` can assert it under bare node.
 *
 * **Type imports only.** `npm test` loads this file with
 * `node --experimental-strip-types`, which cannot resolve the extensionless
 * runtime imports Vite accepts — `source-list.ts` explains the constraint. An
 * `import type` is erased before Node sees it, so it is the one kind allowed.
 */

import type { DashboardBucket, DashboardDays, DashboardRun, ScraperDashboard } from "./api";

/** The windows offered, in the order the switcher draws them. */
export const WINDOWS: { value: DashboardDays; label: string }[] = [
  { value: 1, label: "24 hours" },
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
];

export function windowPhrase(days: DashboardDays): string {
  return days === 1 ? "the last 24 hours" : `the last ${days} days`;
}

/** What a Run in flight is doing, as a sentence a non-engineer can read. */
export function describeActivity(run: DashboardRun): string {
  if (run.status === "queued") return "Waiting for its turn";
  switch (run.phase) {
    case "authenticating":
      return "Signing in to the source";
    case "fetching":
      return run.feeds_total > 0
        ? `Downloading data (${Math.min(run.feeds_fetched, run.feeds_total)} of ${run.feeds_total})`
        : "Downloading data";
    case "expanding":
      return "Downloading player details";
    case "parsing":
      return "Reading the data";
    case "ranking":
      return "Ranking players";
    case "uploading":
      return "Saving the files";
    default:
      return "Starting";
  }
}

/** Who or what started a Run. */
export function triggerLabel(trigger: string): string {
  switch (trigger) {
    case "scheduled":
      return "On schedule";
    case "test":
      return "Test run";
    case "manual":
      return "Started by hand";
    default:
      return trigger;
  }
}

/** How a finished Run ended, in one line. */
export function outcomeSentence(run: DashboardRun): string {
  switch (run.status) {
    case "succeeded": {
      const count = run.player_count ?? 0;
      return `Found ${count.toLocaleString("en-US")} player${count === 1 ? "" : "s"}`;
    }
    case "failed":
      return run.failure_message ?? "Failed";
    case "cancelled":
      return "Stopped before it finished";
    case "skipped":
      return "Nothing new to collect";
    default:
      return run.status;
  }
}

/** `0.9412` → `94%`; nothing decided yet → an em dash, never `0%`. */
export function formatRate(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

/**
 * A success rate's colour. Below nine in ten is worth a second look; below
 * seven in ten is a scraper estate in trouble.
 */
export function rateTone(rate: number | null): "neutral" | "warning" | "danger" {
  if (rate === null || rate >= 0.9) return "neutral";
  return rate >= 0.7 ? "warning" : "danger";
}

/** The Sources list, filtered to one sport — where a Dashboard sport row leads. */
export function sourcesForSport(sport: string): string {
  return `/sources?${new URLSearchParams({ sport })}`;
}

/** `soccer` → `Soccer`. */
export function sportName(sport: string): string {
  return sport.charAt(0).toUpperCase() + sport.slice(1);
}

/** Where a scraper is configured. A Transfer Portal profile has no page of its own. */
export function scraperPath(scraper: { kind: "source" | "transfer_portal"; profile_id: string }): string {
  return scraper.kind === "transfer_portal" ? "/transfer-portal" : `/sources/${scraper.profile_id}`;
}

// --- Coming up ---------------------------------------------------------------

/**
 * When a scheduled run will start, both ways a person asks: how long from now,
 * and at what time on their own clock — "in 3 hr · today at 18:00".
 */
export function whenPhrase(at: string, timeZone: string, now: number = Date.now()): string {
  const moment = new Date(at);
  const wait = moment.getTime() - now;
  const until = wait <= 60_000 ? "any minute" : `in ${roughDuration(wait)}`;

  const parts = (date: Date) =>
    Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
        .formatToParts(date)
        .map((p) => [p.type, p.value]),
    );
  const then = parts(moment);
  const today = parts(new Date(now));
  const tomorrow = parts(new Date(now + 86_400_000));
  const sameDay = (a: Record<string, string>, b: Record<string, string>) =>
    a.year === b.year && a.month === b.month && a.day === b.day;

  const day = sameDay(then, today)
    ? "today"
    : sameDay(then, tomorrow)
      ? "tomorrow"
      : `${then.weekday} ${then.day} ${then.month}`;
  return `${until} · ${day} at ${then.hour}:${then.minute}`;
}

// --- Most common failures ------------------------------------------------------

/** A Failure Reason's code as a heading: `credential_rejected` → `Credential rejected`. */
export function reasonTitle(code: string | null): string {
  if (!code) return "Unclassified failure";
  const spaced = code.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `3 runs across 2 scrapers`, or `1 run` where one scraper is the whole story. */
export function reasonScale(runs: number, scrapers: number): string {
  const runWord = `${runs} run${runs === 1 ? "" : "s"}`;
  return scrapers > 1 ? `${runWord} across ${scrapers} scrapers` : runWord;
}

// --- Needing attention -----------------------------------------------------

/**
 * A length of time said the way a person would: "45 sec", "2 min",
 * "1 hr 5 min", "4 days". Rounder than `format.ts`'s `formatDuration`, which is for
 * people reading one Run closely; this is for people glancing at many.
 */
export function roughDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${Math.max(1, seconds)} sec`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  if (minutes >= 48 * 60) return `${Math.round(minutes / (24 * 60))} days`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

/** Why a running scraper is flagged, with how long it has gone and how long it usually takes. */
export function stuckSentence(run: DashboardRun, now: number = Date.now()): string {
  const since = run.started_at ? roughDuration(now - new Date(run.started_at).getTime()) : null;
  const running = since ? `Running for ${since}` : "Running for a long time";
  return run.typical_duration_ms !== null
    ? `${running} — it usually takes ${roughDuration(run.typical_duration_ms)}.`
    : `${running}, with no earlier run to compare against.`;
}

export interface AttentionCounts {
  failed: number;
  missed: number;
  signIn: number;
  stuck: number;
}

/** How many of each kind of problem the Dashboard lists. */
export function attentionCounts(data: ScraperDashboard): AttentionCounts {
  return {
    failed: data.attention.length,
    missed: data.missed_schedules.length,
    signIn: data.sign_in_problems.length,
    stuck: data.active.filter((run) => run.stuck).length,
  };
}

export function attentionTotal(counts: AttentionCounts): number {
  return counts.failed + counts.missed + counts.signIn + counts.stuck;
}

/** `2 failed · 1 missed its schedule`, naming only the kinds that occur. */
export function attentionBreakdown(counts: AttentionCounts): string {
  const parts = [
    counts.failed && `${counts.failed} failed`,
    counts.missed && `${counts.missed} missed ${counts.missed === 1 ? "its schedule" : "their schedules"}`,
    counts.signIn && `${counts.signIn} sign-in problem${counts.signIn === 1 ? "" : "s"}`,
    counts.stuck && `${counts.stuck} stuck`,
  ].filter((part): part is string => Boolean(part));
  return parts.length ? parts.join(" · ") : "Nothing needs attention";
}

// --- The chart -------------------------------------------------------------

/**
 * The viewer's IANA time zone — `Asia/Karachi`, `America/New_York`. Sent with
 * the request so the server cuts each day at the viewer's midnight; UTC where
 * the browser will not say.
 */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** A zone's short name for a caption: `PKT`, `EDT`, or `GMT+5` where it has none. */
export function zoneName(timeZone: string, at: Date = new Date()): string {
  const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
    .formatToParts(at)
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? timeZone;
}

/**
 * A bucket's axis label, in the zone the server cut the buckets in — so a
 * bar that begins at local midnight is labelled with the local date, rather
 * than the day before for anyone east of Greenwich.
 */
export function bucketLabel(start: string, bucket: "hour" | "day", timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    day: "numeric",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(start));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return bucket === "hour" ? `${part("hour")}:00` : `${part("weekday")} ${part("day")}`;
}

export function bucketTotal(bucket: DashboardBucket): number {
  return bucket.succeeded + bucket.failed + bucket.other;
}

/**
 * The chart's top value: the busiest bucket rounded up to a number worth
 * printing on an axis, and never less than one so an empty window still draws.
 */
export function chartCeiling(buckets: readonly DashboardBucket[]): number {
  const busiest = Math.max(1, ...buckets.map(bucketTotal));
  if (busiest <= 5) return busiest;
  const magnitude = 10 ** Math.floor(Math.log10(busiest));
  for (const step of [1, 2, 2.5, 5, 10]) {
    const ceiling = step * magnitude;
    if (ceiling >= busiest) return ceiling;
  }
  return busiest;
}

/**
 * Which bucket labels to print. Thirty days or twenty-four hours of labels do
 * not fit under a phone-width chart, so every nth is shown, always including
 * the most recent.
 */
export function labelledIndexes(count: number, most = 8): Set<number> {
  const every = Math.max(1, Math.ceil(count / most));
  const shown = new Set<number>();
  for (let i = count - 1; i >= 0; i -= every) shown.add(i);
  return shown;
}
